import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Send, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Numero di telefono e WhatsApp centralizzato
export const PHONE_NUMBER = '+39 379 389 2910';
export const PHONE_LINK = 'tel:+393793892910';
export const WHATSAPP_LINK = 'https://wa.me/393793892910';
export const EMAIL = 'info@mm-webdesigners.it';

// Privacy Policy Modal
const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-[#111] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Privacy Policy
          </DialogTitle>
        </DialogHeader>
        <div className="text-white/70 text-sm space-y-4 pr-4">
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-2">Informativa sul trattamento dei dati personali</h3>
          <p>
            Ai sensi del Regolamento UE n. 2016/679 (GDPR), M&M Web Designers & Visibility Specialists informa che i dati personali raccolti tramite questo sito web saranno trattati nel rispetto della normativa vigente.
          </p>
          
          <h4 className="text-white font-semibold mt-4">Finalità del trattamento</h4>
          <p>I dati personali raccolti saranno utilizzati per:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Rispondere a richieste di informazioni e preventivi</li>
            <li>Fornire i servizi richiesti</li>
            <li>Adempiere ad obblighi legali e contrattuali</li>
            <li>Inviare comunicazioni commerciali (previo consenso)</li>
          </ul>
          
          <h4 className="text-white font-semibold mt-4">Titolare del trattamento</h4>
          <p>
            Titolare del trattamento è <strong className="text-white">M&M Web Designers</strong>
          </p>
          <p>
            Raggiungibile all&apos;indirizzo mail: <a href="mailto:info@mm-webdesigners.it" className="text-[#0ea5e9] hover:underline">info@mm-webdesigners.it</a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Terms Modal
const TermsConditionsModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-[#111] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Termini e Condizioni
          </DialogTitle>
        </DialogHeader>
        <div className="text-white/70 text-sm space-y-4 pr-4">
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-2">1 | Ambito di applicazione</h3>
          <p>
            I presenti Termini e Condizioni disciplinano la fornitura dei servizi offerti da M&M Web Designers & Visibility Specialists.
          </p>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">2 | Oggetto dei servizi</h3>
          <p>
            M&M realizza siti web, web app, gestionali, automazioni e materiali grafici sulla base delle informazioni fornite dal cliente.
          </p>
          
          <h3 className="text-[#0ea5e9] font-semibold text-base mt-4">3 | Pagamenti</h3>
          <p>Modalità standard:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>50% anticipo</li>
            <li>50% alla consegna</li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FooterSection = ({ showNewsletter = true }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    // Simulate newsletter signup
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Iscrizione completata!', {
      description: 'Grazie per esserti iscritto alla nostra newsletter.'
    });
    setEmail('');
    setLoading(false);
  };

  return (
    <>
      <footer className="bg-[#111] border-t border-white/10 py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="lg:col-span-1">
              <Link to="/" className="inline-block mb-4">
                <img 
                  src="/images/logo.png" 
                  alt="M&M Web Designers" 
                  className="h-14 w-auto"
                  data-testid="footer-logo"
                />
              </Link>
              <p className="text-white/50 text-sm mb-6">
                Progettiamo e sviluppiamo sistemi digitali per aziende e professionisti.
              </p>
              <div className="flex gap-4">
                <a 
                  href="https://www.instagram.com/mm_webdesigners.it" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/50 hover:text-[#E4405F] hover:bg-white/10 transition-all"
                >
                  <Instagram size={18} />
                </a>
                <a 
                  href={WHATSAPP_LINK}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/50 hover:text-[#25D366] hover:bg-white/10 transition-all"
                >
                  <MessageCircle size={18} />
                </a>
              </div>
            </div>

            {/* Contatti */}
            <div>
              <h4 className="font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Contatti</h4>
              <ul className="space-y-3 text-sm text-white/50">
                <li>
                  <a href={PHONE_LINK} className="flex items-center gap-2 hover:text-[#0ea5e9] transition-colors">
                    <Phone size={16} />
                    {PHONE_NUMBER}
                  </a>
                </li>
                <li>
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#25D366] transition-colors">
                    <MessageCircle size={16} />
                    WhatsApp
                  </a>
                </li>
                <li>
                  <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 hover:text-[#0ea5e9] transition-colors">
                    <Mail size={16} />
                    {EMAIL}
                  </a>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                  <span>Milano, Sesto San Giovanni</span>
                </li>
              </ul>
            </div>

            {/* Link Utili */}
            <div>
              <h4 className="font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Link Utili</h4>
              <ul className="space-y-3 text-sm text-white/50">
                <li><Link to="/#chi-siamo" className="hover:text-[#0ea5e9] transition-colors">Chi Siamo</Link></li>
                <li><Link to="/#portfolio" className="hover:text-[#0ea5e9] transition-colors">Portfolio</Link></li>
                <li><Link to="/#processo" className="hover:text-[#0ea5e9] transition-colors">Come Lavoriamo</Link></li>
                <li><Link to="/#contatti" className="hover:text-[#0ea5e9] transition-colors">Contatti</Link></li>
              </ul>
            </div>

            {/* Newsletter */}
            {showNewsletter && (
              <div>
                <h4 className="font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Newsletter</h4>
                <p className="text-white/50 text-sm mb-4">
                  Ricevi consigli e novità sul web marketing locale.
                </p>
                <form onSubmit={handleNewsletter} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="La tua email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  />
                  <Button 
                    type="submit" 
                    className="bg-[#0ea5e9] hover:bg-[#0284c7] px-4"
                    disabled={loading}
                  >
                    <Send size={18} />
                  </Button>
                </form>
              </div>
            )}
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © 2026 M&M Web Designers | P. IVA IT13672210963
            </p>
            <div className="flex items-center gap-6">
              <a 
                href="https://www.instagram.com/mm_webdesigners.it" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/40 hover:text-[#E4405F] transition-colors"
              >
                <Instagram size={20} />
              </a>
              <div className="flex gap-6 text-sm text-white/40">
                <button 
                  onClick={() => setShowPrivacy(true)} 
                  className="hover:text-[#0ea5e9] transition-colors"
                >
                  Privacy Policy
                </button>
                <button 
                  onClick={() => setShowTerms(true)} 
                  className="hover:text-[#0ea5e9] transition-colors"
                >
                  Termini e Condizioni
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <PrivacyPolicyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
      <TermsConditionsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
    </>
  );
};

export default FooterSection;
